import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { styles as sharedStyles } from '../styles/shared-styles';
import { resolveRouterPath, router } from '../router';
import { convertToMarkdownTemplateResult } from '../utility/markdown';
import { helpTopicBody, helpTopicExists, helpTopicTitle } from './help-content';
import { orderedHelpSlugs } from './help-manifest';

// A single manual topic at /help/:slug. The slug is passed in as a property from
// the route render so a param change re-renders. Renders the topic's markdown via
// the shared renderer (trimBlanks=false: the content has code fences + nested
// lists whose indentation must be preserved).
@customElement('help-topic-page')
export class HelpTopicPage extends LitElement {
  @property({ type: String }) slug = '';

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      .wrap {
        max-width: 760px;
        margin: 0 auto;
        padding: clamp(1.25rem, 4vw, 2.5rem) clamp(1rem, 4vw, 2rem) 3rem;
        box-sizing: border-box;
      }
      .back {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        text-decoration: none;
        color: var(--sl-color-primary-600);
        font-size: 0.9rem;
        font-weight: 600;
        margin-bottom: 1.25rem;
      }
      .back:hover {
        text-decoration: underline;
      }
      .not-found {
        color: var(--sl-color-neutral-600);
      }

      /* Rendered-markdown typography */
      .md {
        color: var(--sl-color-neutral-900);
        line-height: 1.6;
      }
      .md h1 {
        font-family: var(--font-display, inherit);
        font-size: 1.6rem;
        font-weight: 800;
        letter-spacing: -0.02em;
        margin: 0 0 0.75rem;
      }
      .md h2 {
        font-size: 1.2rem;
        font-weight: 700;
        margin: 1.75rem 0 0.6rem;
        padding-top: 0.4rem;
        border-top: 1px solid var(--sl-color-neutral-200);
      }
      .md h3 {
        font-size: 1.02rem;
        font-weight: 700;
        margin: 1.25rem 0 0.4rem;
      }
      .md p {
        margin: 0.6rem 0;
      }
      .md ul,
      .md ol {
        margin: 0.6rem 0;
        padding-left: 1.4rem;
      }
      .md li {
        margin: 0.25rem 0;
      }
      .md em {
        color: var(--sl-color-neutral-500);
        font-style: italic;
      }
      .md code {
        font-family: var(--sl-font-mono, monospace);
        font-size: 0.86em;
        background: var(--sl-color-neutral-100);
        padding: 0.1em 0.35em;
        border-radius: 4px;
      }
      .md pre {
        background: var(--sl-color-neutral-100);
        padding: 0.8rem 1rem;
        border-radius: 8px;
        overflow-x: auto;
      }
      .md pre code {
        background: none;
        padding: 0;
      }
      .md a {
        color: var(--sl-color-primary-600);
      }
      .md table {
        border-collapse: collapse;
        width: 100%;
        margin: 0.8rem 0;
        font-size: 0.9rem;
      }
      .md th,
      .md td {
        border: 1px solid var(--sl-color-neutral-200);
        padding: 0.4rem 0.6rem;
        text-align: left;
      }
      .md blockquote {
        margin: 0.8rem 0;
        padding: 0.4rem 0.9rem;
        border-left: 3px solid var(--sl-color-primary-300);
        color: var(--sl-color-neutral-600);
      }

      .topic-nav {
        display: flex;
        justify-content: space-between;
        gap: 1rem;
        margin-top: 2.5rem;
        padding-top: 1.25rem;
        border-top: 1px solid var(--sl-color-neutral-200);
      }
      .topic-nav a {
        text-decoration: none;
        color: var(--sl-color-primary-600);
        font-weight: 600;
        font-size: 0.9rem;
        max-width: 48%;
      }
      .topic-nav a.next {
        margin-left: auto;
        text-align: right;
      }
      .topic-nav .label {
        display: block;
        font-size: 0.72rem;
        color: var(--sl-color-neutral-500);
        font-weight: 500;
      }
    `,
  ];

  private _go(e: Event, path: string) {
    e.preventDefault();
    router.navigate(resolveRouterPath(path));
  }

  // Delegated click handler for links inside the rendered markdown. Authors
  // write two kinds of same-origin link: a cross-link to another help topic
  // ("slug.md") and a link to an in-app destination ("/sales/new"). Both are
  // intercepted and routed in-app so a manual link behaves like every other
  // link in the shell instead of triggering a full page reload. External links
  // (http(s), mailto) open in a new tab untouched; modified clicks and in-page
  // anchors are left to the browser.
  private _onContentClick(e: MouseEvent) {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }
    const anchor = (e.target as HTMLElement)?.closest?.('a');
    if (!anchor) return;
    const raw = anchor.getAttribute('href') ?? '';
    if (!raw || raw.startsWith('#')) return; // in-page anchor, leave it
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) {
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      return; // external, open normally
    }
    e.preventDefault();
    const md = url.pathname.match(/\/([^/]+)\.md$/i);
    if (md) {
      router.navigate(resolveRouterPath('help/' + md[1]));
      return;
    }
    router.navigate(url.pathname + url.search);
  }

  private _navLink(slug: string, dir: 'prev' | 'next') {
    return html`
      <a
        class=${dir}
        href=${resolveRouterPath('help/' + slug)}
        @click=${(e: Event) => this._go(e, 'help/' + slug)}
      >
        <span class="label">${dir === 'prev' ? 'Previous' : 'Next'}</span>
        ${helpTopicTitle(slug)}
      </a>
    `;
  }

  render() {
    const slug = this.slug;
    const backLink = html`
      <a
        class="back"
        href=${resolveRouterPath('help')}
        @click=${(e: Event) => this._go(e, 'help')}
      >
        <sl-icon name="arrow-left"></sl-icon> All help topics
      </a>
    `;

    if (!slug || !helpTopicExists(slug)) {
      return html`
        <div class="wrap">
          ${backLink}
          <p class="not-found">
            That help topic could not be found. Pick one from the list.
          </p>
        </div>
      `;
    }

    const ordered = orderedHelpSlugs().filter(helpTopicExists);
    const i = ordered.indexOf(slug);
    const prev = i > 0 ? ordered[i - 1] : null;
    const next = i >= 0 && i < ordered.length - 1 ? ordered[i + 1] : null;

    return html`
      <div class="wrap">
        ${backLink}
        <article class="md" @click=${this._onContentClick}>
          ${convertToMarkdownTemplateResult(helpTopicBody(slug), false)}
        </article>
        ${
          prev || next
            ? html`
                <nav class="topic-nav">
                  ${prev ? this._navLink(prev, 'prev') : nothing}
                  ${next ? this._navLink(next, 'next') : nothing}
                </nav>
              `
            : nothing
        }
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'help-topic-page': HelpTopicPage;
  }
}
